import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

/**
 * Service for managing and storing the previous URL visited in localStorage.
 * This can be useful to implement "back" button functionality in your component,
 * allowing users to return to the previous page they visited.
 */

@Injectable({
  providedIn: 'root',
})
export class PpUrlService {
  private storageKey: string; //  Key for storing URL in localStorage
  private prevURL$: BehaviorSubject<string>;

  constructor() {
    // Retrieve the saved URL from localStorage, if available
    const savedPrevURL = localStorage.getItem(this.storageKey);
    this.prevURL$ = new BehaviorSubject<string>(savedPrevURL ? savedPrevURL : undefined);
  }

  /**
   * Sets the current URL as the previous URL.
   * This method should be called before navigating to a new component.
   *
   * **Example:**
   * Let's say we're currently on the **Fund -> Portfolio -> Insights -> Valuation tab**
   * the url would be '...fund-performances/fund/portfolios/analysis/basis...'
   * If we click on the company, it goes to the company performance page.
   * Now, if we want to come back to the previous page (which in this case is Valuation tab) by clicking **Back** button,
   * we can retrieve this url from the storage and navigate back.
   *
   * @param url - The URL to be stored as the previous URL.
   */
  setPrevURL(url: string): void {
    this.prevURL$.next(url);
    localStorage.setItem(this.storageKey, url);
  }

  /**
   * Gets the previous URL as an observable BehaviorSubject.
   * This can be subscribed to in order to reactively get the current previous URL.
   */
  get prevURL(): BehaviorSubject<string> {
    return this.prevURL$;
  }

  /**
   * Clears the stored previous URL from the BehaviorSubject and localStorage.
   * This method can be used when you want to reset or clear the history.
   */
  clearPrevURL(): void {
    this.prevURL$.next(undefined);
    localStorage.removeItem(this.storageKey);
  }
}
